import { ReactNode, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import Layout from '../components/Layout/Layout';
import Home from '../pages/Home/Home';
import Cases from '../pages/Cases/Cases';
import DataInitializerWithLoading from '../components/DataInitializerWithLoading/DataInitializerWithLoading';
import FullScreenLoading from '../components/FullScreenLoading/FullScreenLoading';
import Documents from '../pages/Documents/Documents';
import ImmigrationStatus from '../pages/ImmigrationStatus/ImmigrationStatus';
import UserProfile from '../pages/UserProfile/UserProfile';
import UserPreferences from '../pages/UserPreferences/UserPreferences';
import CaseDocuments from 'src/pages/CaseDocuments/CaseDocuments';
import { PublicRoutes } from 'src/utils/constants/common';
import PdfPreview from 'src/components/PdfPreview/PdfPreview';
import TwoFactorAuth from 'src/pages/UserPreferences/TwoFactorAuthentication/TwoFactorAuthentication';
import { InterceptorProvider } from 'src/components/PassportClickInterceptor/PassportClickContext';
import MailingAddressConfirmation from 'src/components/MailingAddressConfirmation/MailingAddressConfirmation';
import { ErrorBoundary, RemoteComponent } from 'src/shared/federation/components/RemoteComponent/RemoteComponent';
import AidiWrapper from 'src/components/AidiWrapper/AidiWrapper';
import VisaAndTravel from 'src/pages/VisaAndTravel/VisaAndTravel';
import QuestionnairePrintView from 'src/pages/QuestionnairePrintView/QuestionnairePrintView';

// Create a wrapper component to handle redirects after auth
const AuthRedirectHandler = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const pathname = window.location.pathname;

  // Handle authentication redirect
  useEffect(() => {
    if (PublicRoutes.includes(pathname)) {
      return;
    }

    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  return children;
};

const AppRouter = () => {
  const auth = useAuth();
  const pathname = window.location.pathname;

  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      console.log(`${new Date().toLocaleString()} - refreshing token...`);
      auth.signinSilent().catch((e) => {
        console.error(e);
        auth.signoutRedirect().then(() => {
          window.location.href = '/';
        });
      });
    });
  }, [auth.events, auth.signinSilent]);

  if (auth.error) {
    console.error(`Auth error: ${auth.error.message}`);
    window.location.href = '/';
  }

  if (!PublicRoutes.includes(pathname) && auth.isLoading && !auth.isAuthenticated) return <FullScreenLoading />;

  return (
    <Router>
      <AuthRedirectHandler>
        <Routes>
          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <DataInitializerWithLoading>
                <InterceptorProvider>
                  <Layout>
                    <Routes>
                      <Route path="/home" element={<Home />} />
                      <Route path="/cases" element={<Cases />} />
                      <Route path="/documents" element={<Documents />} />
                      <Route path="/immigration-status" element={<ImmigrationStatus />} />
                      <Route path="/user-profile" element={<UserProfile />} />
                      <Route path="/user-preferences" element={<UserPreferences />} />
                      <Route path="/user-preferences/two-factor" element={<TwoFactorAuth />} />
                      <Route path="/case-documents" element={<CaseDocuments />} />
                      <Route path="/mailing-address-confirmation" element={<MailingAddressConfirmation />} />
                      <Route path="/visa-and-travel" element={<VisaAndTravel />} />
                      <Route
                        path="/aidi/*"
                        element={
                          <ErrorBoundary remoteName="aidi">
                            <AidiWrapper>
                              <RemoteComponent remoteName="aidi" module="Module" />
                            </AidiWrapper>
                          </ErrorBoundary>
                        }
                      />
                      <Route path="/questionnaireprintview/:questionnaireId" element={<QuestionnairePrintView />} />
                      <Route path="*" element={<Navigate to="/home" replace />} />
                    </Routes>
                  </Layout>
                </InterceptorProvider>
              </DataInitializerWithLoading>
            }
          />

          {/* Public routes */}
          <Route path="/pdfPreview" element={<PdfPreview />} />
        </Routes>
      </AuthRedirectHandler>
    </Router>
  );
};

export default AppRouter;
